import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  type AdminContactInviteInput,
  ClientContactRoleSchema,
  CLIENT_CONTACT_ROLES,
} from '@shared';
import { apiPost, ApiError } from '../../lib/api';
import { applyFieldErrors } from '../../lib/form-errors';

// Client-side resolver: mirrors AdminContactInviteInputSchema but treats an
// empty-string `phone` as "absent" so an untouched optional field doesn't
// trip the `.min(5)` rule. The server is still the canonical validator.
const InviteResolverSchema = z.object({
  email: z.string().email('Enter a valid email').toLowerCase(),
  name: z.string().min(1, 'Full name is required').max(120),
  contact_role: ClientContactRoleSchema,
  phone: z
    .union([z.string().length(0), z.string().min(5).max(40)])
    .optional(),
});

interface InviteContactModalProps {
  clientId: string;
  clientName: string;
  onClose: () => void;
  onInvited: (invitedEmail: string) => void;
}

export function InviteContactModal({
  clientId,
  clientName,
  onClose,
  onInvited,
}: InviteContactModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const firstFocusable = useRef<HTMLButtonElement>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<AdminContactInviteInput>({
    resolver: zodResolver(InviteResolverSchema),
    defaultValues: {
      email: '',
      name: '',
      contact_role: 'Director',
      phone: '',
    },
  });

  // Escape closes + body-scroll-lock — same modal-hygiene pattern as the
  // edit drawer. Focus the close button on open so SR users can dismiss.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstFocusable.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    };
  }, [onClose, isSubmitting]);

  const onSubmit = async (data: AdminContactInviteInput) => {
    if (justSaved) return;
    setSubmitError(null);
    // Strip empty-string optionals so the server doesn't see `phone: ''` and
    // fail the `min(5)` on the optional. Mirrors ClientCreate's payload-shaping.
    const payload: Record<string, unknown> = { ...data };
    if (payload.phone === '' || payload.phone === null) delete payload.phone;
    try {
      const r = await apiPost<{ user: { email: string } }>(
        `/api/clients/${clientId}/contacts`,
        payload,
      );
      onInvited(r.user.email);
      setJustSaved(true);
      autoCloseTimer.current = setTimeout(() => {
        autoCloseTimer.current = null;
        onClose();
      }, 1200);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: { code?: string; message?: string; detail?: unknown } } | null;
        const code = body?.error?.code;
        if (err.status === 400 && code === 'validation_failed') {
          const handled = applyFieldErrors<AdminContactInviteInput>(body?.error?.detail, setError);
          if (!handled) setSubmitError(err.serverMessage);
          return;
        }
        if (err.status === 409 && code === 'email_in_use') {
          setError('email', {
            type: 'server',
            message: 'A user with this email already exists',
          });
          return;
        }
        if (err.status === 404) {
          setSubmitError('Client no longer exists. Refresh the page.');
          return;
        }
        setSubmitError(err.serverMessage);
      } else {
        setSubmitError('Failed to invite contact. Please try again.');
      }
    }
  };

  return (
    <>
      <div
        className="drawer-backdrop"
        onClick={() => !isSubmitting && onClose()}
        aria-hidden="true"
      />
      <div
        className="invite-contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-contact-h"
      >
        <header className="invite-contact-hd">
          <div>
            <h2 id="invite-contact-h" className="drawer-title">Invite contact</h2>
            <div className="drawer-meta">
              <span>{clientName}</span>
            </div>
          </div>
          <button
            ref={firstFocusable}
            type="button"
            className="drawer-close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            ×
          </button>
        </header>

        <form className="invite-contact-body" onSubmit={handleSubmit(onSubmit)} noValidate>
          <p className="client-create-help" style={{ margin: 0 }}>
            They will receive a no-reply sign-in email from{' '}
            <code>no-reply@</code>. Their account is created in <strong>active</strong> status,
            so they can request a magic code immediately.
          </p>

          <div className="field">
            <label htmlFor="invite-name">Full name<span className="req">*</span></label>
            <input
              id="invite-name"
              type="text"
              autoComplete="name"
              disabled={isSubmitting}
              aria-invalid={errors.name ? 'true' : 'false'}
              {...register('name')}
            />
            {errors.name && <div className="client-create-err">{errors.name.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="invite-email">Corporate email<span className="req">*</span></label>
            <input
              id="invite-email"
              type="email"
              autoComplete="email"
              disabled={isSubmitting}
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email')}
            />
            {errors.email && <div className="client-create-err">{errors.email.message}</div>}
          </div>

          <div className="field">
            <label htmlFor="invite-role">Contact role<span className="req">*</span></label>
            <select
              id="invite-role"
              disabled={isSubmitting}
              {...register('contact_role')}
            >
              {CLIENT_CONTACT_ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {errors.contact_role && (
              <div className="client-create-err">{errors.contact_role.message}</div>
            )}
          </div>

          <div className="field">
            <label htmlFor="invite-phone">Phone</label>
            <input
              id="invite-phone"
              type="tel"
              autoComplete="tel"
              placeholder="Optional"
              disabled={isSubmitting}
              {...register('phone')}
            />
            {errors.phone && <div className="client-create-err">{errors.phone.message}</div>}
          </div>

          {submitError && (
            <div className="client-create-banner" role="alert">
              {submitError}
            </div>
          )}
          {justSaved && (
            <div
              role="status"
              aria-live="polite"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                borderRadius: 'var(--r-sm)',
                background: 'color-mix(in oklab, var(--green, #1f8a4c) 12%, transparent)',
                color: 'var(--green, #1f8a4c)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <span aria-hidden="true">✓</span>
              <span>Invite sent.</span>
            </div>
          )}

          <div className="invite-contact-actions">
            <button
              type="button"
              className="btn ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {justSaved ? 'Close now' : 'Cancel'}
            </button>
            <button type="submit" className="btn accent" disabled={isSubmitting || justSaved}>
              {isSubmitting ? 'Inviting…' : justSaved ? 'Sent ✓' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
